// SimpleFees (B7) — 검차정산 · /main/s/fees. 신고 371 결정 8: 제목은 shellTabSettlement
// ("검차정산") — simpleFeesTitle 미사용. 평가사별 소계 섹션은 넣지 않는다(Jin 2026-09-16
// 삭제 결정) — 평가사 선택은 요약 2칸을 그 범위로 재집계하는 것으로 대체한다.
// 앱 정본: simple_fees_screen.dart `_pickStatus`/`_visibleItems` · simple_fees_controller.dart
// `filterByInspector`/`resolveInspectorForMonth`. 상태 필터 + 평가사 필터 모두 순수 클라
// 필터(재조회 없음) — 상태 → 평가사 순으로 AND 결합, 요약 2칸은 그 결과로 재집계한다.
// 월 변경 시 상태 선택은 그대로 유지(월에 무관한 고정 값이라 리셋 정책이 없다).
// 데스크톱(≥1024, 신고 371 §8 결정 5·9·10) 은 같은 데이터·필터로 screens/fees/FeesDesktop 을
// 그린다(요약 4칸 + 표 + 평가사별 소계 사이드). ≤1023 렌더 트리는 건드리지 않는다.
import React, { useEffect, useState } from 'react';
import { T, R, FONT, S } from '../../theme';
import { useT, useLocale } from '../../../i18n/I18nProvider';
import { useIsMobile } from '../../../utils/useMobile';
import { companySettlement, feeDefault } from '../api';
import { fmtDate, fmtKRW, currentMonth, isolate } from '../fmt';
import { useIsDesktop } from '../useDesktop';
import { describeSimpleError } from '../errors';
import MonthNav from '../parts/MonthNav';
import SimpleCard from '../parts/SimpleCard';
import MoneyKRW from '../parts/MoneyKRW';
import LtrText from '../parts/LtrText';
import SimpleStatusPill from '../parts/SimpleStatusPill';
import ErrorText from '../parts/ErrorText';
import { SheetFrame } from '../parts/ConfirmSheet';
import FeesDesktop from './fees/FeesDesktop';

/// 월 변경 시 선택된 평가사 유지 정책 — 새 달의 by_inspector 에 없으면 전체(null)로.
/// 선택 자체는 호출자(state)가 그대로 보존한다 — 원래 있던 달로 돌아오면 자연히 복원.
function resolveInspectorForMonth(selected, byInspector) {
  if (selected == null) return null;
  var exists = (byInspector || []).some(function (e) { return e.inspector_id === selected; });
  return exists ? selected : null;
}

var page = { display: 'flex', flexDirection: 'column', gap: 12 };
var headRow = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' };
// 데스크톱은 시안 D 처럼 제목 바로 옆에 월 이동을 붙인다(모바일은 제목 좌 / 월 pill 우 그대로).
var headRowDesktop = Object.assign({}, headRow, { justifyContent: 'flex-start', gap: 14 });
var title = { fontSize: 19, fontWeight: 800, letterSpacing: '-.35px', color: T.navy, margin: 0 };

var sumWrap = { display: 'flex', alignItems: 'stretch' };
var sumCell = { flex: 1, display: 'flex', flexDirection: 'column', gap: 4, padding: '14px 16px', minWidth: 0 };
var sumDivider = { width: 1, background: T.bd, margin: '10px 0' };
var sumLabel = { fontSize: FONT.label, fontWeight: 700, color: T.muted };
var sumValue = { fontSize: 20, fontWeight: 800, color: T.ink, letterSpacing: '-.4px' };

var chipsRow = { display: 'flex', flexWrap: 'wrap', gap: 8 };
var chip = { display: 'inline-flex', alignItems: 'center', gap: 6, height: 30, padding: '0 12px', borderRadius: R.pill,
  border: '1px solid ' + T.bd, background: T.card, color: T.body, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
var chipOn = Object.assign({}, chip, { border: '1px solid ' + T.accentBd, background: T.accentTint, color: T.accentPress });

var sheetRow = { display: 'block', width: '100%', textAlign: 'start', padding: '11px 12px', borderRadius: R.md,
  fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: '1px solid transparent', background: 'none', color: T.body };
var sheetRowOn = Object.assign({}, sheetRow, { background: T.accentTint, border: '1px solid ' + T.accentBd, color: T.accentPress });

var tableWrap = { overflowX: 'auto' };
var table = { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 };
var th = { textAlign: 'start', padding: '9px 12px', fontSize: 11, fontWeight: 700, color: T.muted, borderBottom: '1px solid ' + T.bd, whiteSpace: 'nowrap' };
var thRight = Object.assign({}, th, { textAlign: 'end' });
var td = { padding: '10px 12px', borderBottom: '1px solid ' + T.bdSoft, color: T.ink, verticalAlign: 'middle' };
var tdRight = Object.assign({}, td, { textAlign: 'end' });

var mCard = { display: 'flex', flexDirection: 'column', gap: 5, padding: '12px 14px', borderBottom: '1px solid ' + T.bdSoft };
var mHead = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 };
var mTitle = { fontSize: 13, fontWeight: 700, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
var mMeta = { display: 'flex', gap: 12, fontSize: 11.5, color: T.muted, flexWrap: 'wrap' };
var mFoot = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 };

var noteStyle = { fontSize: 11.5, color: T.faint, textAlign: 'center', padding: '10px 4px' };

function carTitleOf(it) {
  var parts = [(it && it.car_model) || '-'];
  if (it && it.car_detail) parts.push(it.car_detail);
  return parts.join(' ');
}

// 상태 필터 라벨 — 앱 `_statusFilterLabel` 그대로(completed=결정 대기 문구 재사용).
function statusFilterLabel(t, status) {
  if (status === 'completed') return t('simpleGroupActionNeeded');
  if (status === 'decided') return t('evalStatusDecided');
  if (status === 'rejected') return t('evalStatusRejected');
  return t('evalFilterAll');
}

var STATUS_OPTIONS = [null, 'completed', 'decided', 'rejected'];

/// 보이는 items 의 검차비 최빈값 — /fee-default 가 없을 때의 단가 폴백. 없으면 null(칸 숨김).
function modeFee(list) {
  var seen = {}, best = 0, bestN = 0;
  (list || []).forEach(function (it) {
    var v = Number((it && it.fee) || 0);
    if (!v) return;
    seen[v] = (seen[v] || 0) + 1;
    if (seen[v] > bestN) { bestN = seen[v]; best = v; }
  });
  return best || null;
}

function SimpleFees() {
  var t = useT();
  var loc = useLocale();
  var isMobile = useIsMobile();
  var isDesktop = useIsDesktop();

  var st1 = useState(currentMonth()); var ym = st1[0], setYm = st1[1];
  var st2 = useState(null); var data = st2[0], setData = st2[1];
  var st4 = useState(null); var error = st4[0], setError = st4[1];
  var st5 = useState(null); var selectedInspector = st5[0], setSelectedInspector = st5[1];
  var st6 = useState(false); var sheetOpen = st6[0], setSheetOpen = st6[1];
  var st7 = useState(null); var selectedStatus = st7[0], setSelectedStatus = st7[1];
  var st8 = useState(false); var statusSheetOpen = st8[0], setStatusSheetOpen = st8[1];
  var st9 = useState(null); var baseFee = st9[0], setBaseFee = st9[1];

  useEffect(function () {
    var alive = true;
    setError(null);
    companySettlement({ month: ym }).then(function (body) {
      if (!alive) return;
      setData(body || {});
    }).catch(function (err) {
      if (!alive) return;
      setError(err);
    });
    return function () { alive = false; };
  }, [ym, setData, setError]);

  // 기본 검차비 — 데스크톱 요약 4번째 칸에서만 쓴다. 실패는 무시(칸이 폴백/숨김으로 간다).
  useEffect(function () {
    if (!isDesktop) return undefined;
    var alive = true;
    feeDefault().then(function (body) {
      if (alive) setBaseFee(Number((body && body.data && body.data.baseFee) || 0) || null);
    }).catch(function () {
      if (alive) setBaseFee(null);
    });
    return function () { alive = false; };
  }, [isDesktop, setBaseFee]);

  var items = (data && data.items) || [];
  var byInspector = (data && data.by_inspector) || [];
  var effectiveInspector = resolveInspectorForMonth(selectedInspector, byInspector);
  var byStatusItems = selectedStatus == null ? items
    : items.filter(function (it) { return it.status === selectedStatus; });
  var visibleItems = effectiveInspector == null ? byStatusItems
    : byStatusItems.filter(function (it) { return it.inspector_id === effectiveInspector; });
  var summaryCount = visibleItems.length;
  var summaryTotal = visibleItems.reduce(function (s, it) { return s + Number((it && it.fee) || 0); }, 0);
  var currentInspectorRow = effectiveInspector == null ? null
    : byInspector.filter(function (e) { return e.inspector_id === effectiveInspector; })[0];
  var chipLabel = currentInspectorRow ? (currentInspectorRow.inspector_name || '-') : t('simpleB1InspectorAll');
  var isEmptyMonth = !!data && items.length === 0;
  // 데스크톱 상태 칩 카운트 — 평가사 필터까지만 적용한 집합(칩을 누르면 실제로 남는 수와 같다).
  var byInspectorItems = effectiveInspector == null ? items
    : items.filter(function (it) { return it.inspector_id === effectiveInspector; });
  var statusCounts = { all: byInspectorItems.length, completed: 0, decided: 0, rejected: 0 };
  byInspectorItems.forEach(function (it) {
    if (statusCounts[it.status] != null) statusCounts[it.status] += 1;
  });

  return (<div className="ss-fees" style={page}>
    <div style={isDesktop ? headRowDesktop : headRow}>
      <h2 style={title}>{t('shellTabSettlement')}</h2>
      <MonthNav ym={ym} onChange={setYm} />
    </div>

    {error ? <ErrorText>{describeSimpleError(t, error)}</ErrorText> : null}

    {!error && !data ? <div style={S.emptyText}>{t('commonLoading')}</div> : null}

    {!error && data && isDesktop ? (<FeesDesktop
      items={visibleItems} byInspector={byInspector} count={summaryCount} total={summaryTotal}
      unitFee={baseFee || modeFee(visibleItems)} statusCounts={statusCounts} isEmptyMonth={isEmptyMonth}
      selectedStatus={selectedStatus} onStatus={setSelectedStatus}
      selectedInspector={effectiveInspector} onInspector={setSelectedInspector} />) : null}

    {!error && data && !isDesktop ? (<React.Fragment>
      <div style={chipsRow}>
        <button type="button" data-testid="ss-fees-status-chip"
          style={selectedStatus != null ? chipOn : chip}
          onClick={function () { setStatusSheetOpen(true); }}>{statusFilterLabel(t, selectedStatus)}</button>
        {byInspector.length ? (
          <button type="button" data-testid="ss-fees-inspector-chip"
            style={effectiveInspector != null ? chipOn : chip}
            onClick={function () { setSheetOpen(true); }}>{chipLabel}</button>
        ) : null}
      </div>

      {!isEmptyMonth ? (<SimpleCard>
        <div style={sumWrap}>
          <div style={sumCell}>
            <span style={sumLabel}>{t('simpleFeeCountLabel')}</span>
            <span style={sumValue}>{t('simpleInspectionsCount', { count: summaryCount })}</span>
          </div>
          <div style={sumDivider} />
          <div style={sumCell}>
            <span style={sumLabel}>{t('simpleFeeTotal')}</span>
            <MoneyKRW won={summaryTotal} style={sumValue} />
          </div>
        </div>
      </SimpleCard>) : null}

      {isEmptyMonth ? (<SimpleCard>
        <div style={S.emptyText}>{t('simpleFeeEmpty')}</div>
      </SimpleCard>) : (isMobile ? (<SimpleCard>
        {visibleItems.map(function (it) {
          return (<div key={it.id} style={mCard}>
            <div style={mHead}>
              <span style={mTitle}>{carTitleOf(it)}</span>
              <SimpleStatusPill status={it.status} />
            </div>
            <LtrText style={{ fontSize: 12, color: T.muted }}>{it.car_number || '-'}</LtrText>
            <div style={mMeta}>
              <span>{t('simpleFeeInspectDate', { date: isolate(fmtDate(loc.locale, it.inspect_date) || '-') })}</span>
              <span>{it.inspector_name || '-'}</span>
            </div>
            <div style={mFoot}>
              <span style={{ fontSize: 12, color: T.muted }}>{t('simpleFeeLabel')}</span>
              <MoneyKRW won={it.fee} style={{ fontSize: 14, fontWeight: 800 }} />
            </div>
          </div>);
        })}
      </SimpleCard>) : (<SimpleCard>
        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('evalTableColCar')}</th>
                <th style={th}>{t('evalFormCarNumber')}</th>
                <th style={th}>{t('evalTableColDate')}</th>
                <th style={th}>{t('evalTableColInspector')}</th>
                <th style={th}>{t('evalTableColStatus')}</th>
                <th style={thRight}>{t('simpleFeeLabel')}</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map(function (it) {
                return (<tr key={it.id}>
                  <td style={td}>{carTitleOf(it)}</td>
                  <td style={td}><LtrText>{it.car_number || '-'}</LtrText></td>
                  <td style={td}><LtrText>{fmtDate(loc.locale, it.inspect_date) || '-'}</LtrText></td>
                  <td style={td}>{it.inspector_name || '-'}</td>
                  <td style={td}><SimpleStatusPill status={it.status} /></td>
                  <td style={tdRight}><MoneyKRW won={it.fee} /></td>
                </tr>);
              })}
            </tbody>
          </table>
        </div>
      </SimpleCard>))}

      <div style={noteStyle}>{t('simpleFeeNote')}</div>
    </React.Fragment>) : null}

    {isDesktop ? null : (<React.Fragment>
    <SheetFrame open={sheetOpen} title={t('simpleB1InspectorPill')} onClose={function () { setSheetOpen(false); }}>
      <button type="button" data-testid="ss-fees-inspector-row"
        style={effectiveInspector == null ? sheetRowOn : sheetRow}
        onClick={function () { setSelectedInspector(null); setSheetOpen(false); }}>{t('simpleB1InspectorAll')}</button>
      {byInspector.map(function (e) {
        var on = effectiveInspector === e.inspector_id;
        return (<button key={e.inspector_id} type="button" data-testid="ss-fees-inspector-row"
          style={on ? sheetRowOn : sheetRow}
          onClick={function () { setSelectedInspector(e.inspector_id); setSheetOpen(false); }}>
          {(e.inspector_name || '-') + '  ·  ' + t('simpleInspectionsCount', { count: e.count }) + '  ·  ' + fmtKRW(e.total)}
        </button>);
      })}
    </SheetFrame>

    <SheetFrame open={statusSheetOpen} title={t('filterSheetStatusTitle')} onClose={function () { setStatusSheetOpen(false); }}>
      {STATUS_OPTIONS.map(function (opt) {
        var on = selectedStatus === opt;
        return (<button key={opt || 'all'} type="button" data-testid="ss-fees-status-row"
          style={on ? sheetRowOn : sheetRow}
          onClick={function () { setSelectedStatus(opt); setStatusSheetOpen(false); }}>{statusFilterLabel(t, opt)}</button>);
      })}
    </SheetFrame>
    </React.Fragment>)}
  </div>);
}

export default SimpleFees;
